/**
 * Cloudflare Web Analytics beacon for the marketing surface (DPIA P12).
 *
 * Cookie-free page and Core Web Vitals statistics under the PECR
 * statistical-purposes exception. It runs unless the visitor objects, so the
 * decision is made once per page load, here, and nowhere else injects it:
 *
 * - production host only, and only with a build-time token (previews,
 *   staging and dev get none);
 * - a guest whose first page is a public marketing route (`inSitemap` in the
 *   route manifest) or `/register`; never an app, token or admin page;
 * - not after an objection: Reject in the analytics banner, recorded on this
 *   device, or the browser's Global Privacy Control signal.
 *
 * `spa: false` measures only the page that loaded the beacon, so moving on
 * into the app is not reported. An objection takes effect from the next
 * page load: a script that is already running cannot be unloaded.
 */
import { readAnalyticsChoice } from "@/lib/consent/analyticsChoice";
import { PRODUCTION_ORIGIN, routeManifest } from "@/routing/manifest";

const BEACON_SRC = "https://static.cloudflareinsights.com/beacon.min.js";

const MARKETING_PATHS: ReadonlySet<string> = new Set([
  ...routeManifest.flatMap((route) =>
    route.kind === "view" && route.inSitemap ? [route.path] : [],
  ),
  "/register",
]);

export interface BeaconContext {
  token: string | undefined;
  hostname: string;
  path: string;
  signedIn: boolean;
  objected: boolean;
  globalPrivacyControl: boolean;
}

export function beaconAllowed(context: BeaconContext): boolean {
  return (
    Boolean(context.token) &&
    context.hostname === new URL(PRODUCTION_ORIGIN).hostname &&
    !context.signedIn &&
    MARKETING_PATHS.has(context.path) &&
    !context.objected &&
    !context.globalPrivacyControl
  );
}

/** Add the beacon to this page if `beaconAllowed` says so. */
export function installCloudflareBeacon(options: {
  path: string;
  signedIn: boolean;
}): boolean {
  const token = import.meta.env.VITE_CF_BEACON_TOKEN as string | undefined;
  const nav = navigator as Navigator & { globalPrivacyControl?: boolean };
  const allowed = beaconAllowed({
    token,
    hostname: window.location.hostname,
    path: options.path,
    signedIn: options.signedIn,
    objected: readAnalyticsChoice(null) === "rejected",
    globalPrivacyControl: nav.globalPrivacyControl === true,
  });
  if (!allowed || document.querySelector(`script[src="${BEACON_SRC}"]`)) {
    return false;
  }
  const script = document.createElement("script");
  script.defer = true;
  script.src = BEACON_SRC;
  script.dataset.cfBeacon = JSON.stringify({ token, spa: false });
  document.body.appendChild(script);
  return true;
}
